import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { useToast } from "../../hooks/use-toast";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { useForm, FormProvider } from "react-hook-form";
import {
  ArrowLeft,
  Clock,
  User,
  Flag,
  Lightbulb,
  Check,
  X,
  FileText,
  Bot,
  BotMessageSquare,
  HelpCircle,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
function ClaimView({ claim, onBack, onUpdateStatus = () => {} }) {
  const { toast } = useToast();
  const methods = useForm();
  const [isRejected, setIsRejected] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [agentNotes, setAgentNotes] = useState("");
  const [openSuggestionModal, setOpenSuggestionModal] = useState(false);
  const DOCUMENT_OPTIONS = [
    { value: "invoice", label: "Purchase Invoice" },
    { value: "idProof", label: "ID Proof" },
    { value: "warrantyCard", label: "Warranty Card" },
  ];
  const rejectionMessage = `Dear Sagar,\n
Thank you for submitting your claim regarding the dishwasher (Plan ID: 12345), purchased on 11-06-2025.\n
After thoroughly reviewing the details of your claim, we regret to inform you that it has been declined under Section 16.1 of your Extended Warranty Plan – Gold.\n
“Failures due to normal wear and tear, corrosion, or gradual deterioration of the covered product are not eligible for reimbursement.”\n
According to the technician’s assessment, the water drain-out issue appears to be the result of gradual wear and sediment build-up, which falls under normal usage conditions and is not considered a sudden mechanical failure.\n
We understand this may be disappointing. If you believe this assessment is inaccurate or if new information becomes available, you are welcome to submit a request for re-evaluation. We will be happy to review the claim again.\n
Best regards,
xaxathrive Claims Department`;
  const [acceptReason, setAcceptReason] = useState(
    "Claim accepted for repair/replacement."
  );
  const [acceptMessage, setAcceptMessage] = useState(`Dear Sagar,\n
Thank you for submitting your claim regarding the dishwasher (Plan ID: 12345), purchased on 11-06-2025.\n
We are pleased to inform you that your claim has been **approved** under the terms of your Extended Warranty Plan – Gold.\n
Based on our review and the technician's assessment, the reported issue qualifies for coverage. You are now authorized to proceed with repair or replacement, up to the policy’s specified coverage limits.\n
Our service team will follow up shortly with the next steps.\n
Best regards,  
xaxathrive Claims Department`);
  const agentSuggestions = [
    "The claim appears valid based on submitted evidence.",
    "Customer has provided sufficient documentation for approval.",
    "Request additional information to verify damage.",
  ];
  const [showRequestInfoForm, setShowRequestInfoForm] = useState(false);
  const [infoTitle, setInfoTitle] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [requiredDocs, setRequiredDocs] = useState("warrantyCard");
  const [requestConfirmationMsg, setRequestConfirmationMsg] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [isRequestSubmitted, setIsRequestSubmitted] = useState(false);
  const triageData = [
    {
      question: "Any unusual incident during installation or recently?",
      answer: "No",
    },
    { question: "Filter or drain hose cleaned?", answer: "Yes" },
    { question: "Any error code on display panel?", answer: "Yes" },
    { question: "Power supply stable?", answer: "Yes" },
    { question: "Any recent plumbing changes in kitchen?", answer: "Yes" },
    {
      question: "Is water remaining in the bottom of the dishwasher post-cycle",
      answer: "Yes",
    },
  ];
  function formatDateToCustomString(date) {
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" }); // June
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }
  function formatDateToCustomString(date) {
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" }); // June
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }
  const getDateAfterDaysFormatted = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return formatDateToCustomString(date);
  };
  const [responseTime, setResponseTime] = useState(
    getDateAfterDaysFormatted(3)
  );
  const handleAcceptSubmit = () => {
    if (!acceptReason.trim()) {
      toast({
        title: "Approval reason required",
        description: "Please provide an acceptance note.",
        variant: "error",
      });
      return;
    }

    // Call your update function
    onUpdateStatus("approved", acceptReason);

    setIsApproved(true);
    setIsRejected(false);
    setShowAcceptForm(false);

    toast({
      title: "Claim Approved",
      description: "The claim has been marked as approved.",
      variant: "success",
    });
  };

  const handleRejectSubmit = () => {
    if (!rejectionMessage.trim()) {
      toast({
        title: "Rejection Message Required",
        description: "Please provide a valid rejection message.",
        variant: "error",
      });
      return;
    }

    // If onUpdateStatus is provided
    if (typeof onUpdateStatus === "function") {
      onUpdateStatus("rejected", rejectionMessage);
    }

    setIsRejected(true);
    setIsApproved(false);
    setShowRejectForm(false);
    setRejectReason(rejectionMessage); // optional for later display

    toast({
      title: "Claim Rejected",
      description: "The claim has been marked as rejected.",
      variant: "error",
    });
  };
  const handleSubmitRequestInfo = () => {
    if (!infoTitle || !infoMessage) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill in all required fields before submitting.",
        variant: "warning",
      });
      return;
    }

    const requestPayload = {
      title: infoTitle,
      message: infoMessage,
      documents: requiredDocs,
      expectedBy: responseTime,
      internalNotes,
    };

    // Call backend or state update here
    console.log("Request More Info Submitted", requestPayload);

    toast({
      title: "Request Sent",
      description: "Customer will be notified to provide more details.",
      variant: "warning",
    });
    setRequestConfirmationMsg(
      `Requested additional information from the customer — specifically the ${requiredDocs
        .replace(/([A-Z])/g, " $1")
        .toLowerCase()} to be provided within 3 days to proceed with claim verification.`
    );
    setIsRequestSubmitted(true);
    // Reset form
    setShowRequestInfoForm(false);
    setInfoTitle("");
    setInfoMessage("");
    setRequiredDocs([]);
    setResponseTime("");
    setInternalNotes("");
  };
  useEffect(() => {
    setInfoTitle("Need warranty card");
    setInfoMessage(
      "Kindly provide the warranty card to proceed with your claim verification."
    );
    setInternalNotes(
      "Requested warranty card to validate product coverage and purchase date."
    );
  }, []);
  useEffect(() => {
    if (claim?.status === "approved") {
      setIsApproved(true);
      setIsRejected(false);
    } else if (claim?.status === "rejected") {
      setIsRejected(true);
      setIsApproved(false);
    }
  }, [claim?.status]);
  const handleApprove = () => onUpdateStatus("approved");
  const handleReject = () => onUpdateStatus("rejected");
  const handleRequestInfo = () => onUpdateStatus("pending");
  const { claimId } = useParams();

  const imagePaths = [1, 2].map((i) => `/images/${claimId}/img${i}.jpg`);

  useEffect(() => {
    debugger;
    console.log(imagePaths);
  }, [imagePaths, claimId]);

  const dummyData = {
    "Samsung-Dishwasher": {
      name: "Scot deeley",
      email: "Scot@deeleygroup.com",
      confidenceScore: 92,
      riskLevel: "Low",
      claimId: "CL-2025-001",
      product: "Samsung Dishwasher",
      issutype: "Water not Draining",
      ClaimAmount: 1000,
    },

    "Samsung-Refrigerator": {
      name: "chris defour",
      email: "chris.defour@devbuddy.ca",
      confidenceScore: 55,
      riskLevel: "Medium",
      claimId: "CL-2025-002",
      product: "Samsung Refrigerator",
      issutype: "Not Powering on",
      ClaimAmount: 200,
    },
    "iphone-16": {
      name: "Mark",
      email: "mark@gmail.com",
      confidenceScore: 25,
      riskLevel: "High",
      claimId: "CL-2025-003",
      product: "iphone 16",
      issutype: "Screen broken",
      ClaimAmount: 0,
    },
  };

  const claimData = dummyData[claimId];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/claims">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={16} />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Claim Details</h1>
          <p className="text-slate-600">Review AI analysis and make decision</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* AI Analysis Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* AI Analysis Summary */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">AI Analysis Summary</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-success rounded-full"></div>
                  <span className="text-sm text-slate-600">
                    Analysis Complete
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900">
                    {claimData?.confidenceScore !== undefined
                      ? `${claimData.confidenceScore.toFixed(1)}%`
                      : "N/A"}
                  </div>
                  <div className="text-sm text-slate-600">Confidence Score</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-success">Valid</div>
                  <div className="text-sm text-slate-600">Plan Status</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <div
                    className={`text-2xl font-bold ${
                      claimData?.riskLevel === "Low"
                        ? "text-green-600"
                        : claimData?.riskLevel === "Medium"
                        ? "text-yellow-500"
                        : claimData?.riskLevel === "High"
                        ? "text-red-600"
                        : "text-gray-400"
                    }`}
                  >
                    {claimData?.riskLevel || "Unknown"}
                  </div>

                  <div className="text-sm text-slate-600">Risk Level</div>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  {
                    title: "Protection Plan Validation",
                    description:
                      "Plan is active and covers incidents. Customer has valid coverage.",
                    icon: Check,
                    color: "success",
                  },
                  {
                    title: "Evidence Review",
                    description:
                      "Description matches claim type. Damage timeline is consistent with customer report.",
                    icon: Check,
                    color: "success",
                  },
                  {
                    title: "Pricing Verification",
                    description: `Claimed amount ($500) requires verification for this device type.`,
                    icon: Flag,
                    color: "warning",
                  },
                ].map((item, index) => (
                  <div className="flex items-start gap-3" key={index}>
                    <div
                      className={`w-6 h-6 bg-${item.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}
                    >
                      <item.icon className="text-white" size={12} />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">
                        {item.title}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Evidence Gallery */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Submitted Evidence</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {imagePaths.map((src, idx) => (
                  <div
                    key={idx}
                    className="aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-75 transition-opacity flex items-center justify-center"
                  >
                    <img
                      src={src}
                      alt={`Evidence ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}

                {/* Always show the Purchase Receipt card */}
                <div className="aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-75 transition-opacity flex items-center justify-center">
                  <div className="text-center">
                    <FileText
                      className="text-slate-400 mx-auto mb-2"
                      size={32}
                    />
                    <p className="text-sm text-slate-600">Purchase Receipt</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bot className="text-blue-500" size={20} />
                <CardTitle className="text-lg">
                  AI Analysis & Recommendations
                </CardTitle>
                <Badge variant="secondary" className="ml-auto">
                  Analysis Complete
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* AI Brief */}

              {/* <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">
                  AI Analysis & Recommendations
                </h4>
                <p className="text-sm text-slate-700">Analysis Complete</p>
              </div> */}

              {/* AI Recommendations */}
              <div className="space-y-3">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Lightbulb className="text-blue-500" size={16} />
                  AI Recommendations
                </h4>

                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm">
                    <Check
                      className="text-green-500 mt-0.5 flex-shrink-0"
                      size={14}
                    />
                    <span className="text-slate-700">
                      Coverage verification: Covered under Protection Plan
                      Policy Section 4.2 – Drainage System
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check
                      className="text-green-500 mt-0.5 flex-shrink-0"
                      size={14}
                    />
                    <span className="text-slate-700">
                      Evidence quality: Technician report and images confirm
                      drain malfunction
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check
                      className="text-green-500 mt-0.5 flex-shrink-0"
                      size={14}
                    />
                    <span className="text-slate-700">
                      Policy compliance: Reported within 90-day policy
                      requirement
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check
                      className="text-green-500 mt-0.5 flex-shrink-0"
                      size={14}
                    />
                    <span className="text-slate-700">
                      Recommended action: Approve for repair or part replacement
                      within coverage limits
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check
                      className="text-green-500 mt-0.5 flex-shrink-0"
                      size={14}
                    />
                    <span className="text-slate-700">
                      Next steps: Customer to receive repair authorization
                      within 24 hours
                    </span>
                  </li>
                </ul>

                {/* Agent Override Section */}
                <div className="mt-6 p-4 border border-slate-200 rounded-lg">
                  <h5 className="font-medium text-slate-900 mb-3">
                    Agent Decision
                  </h5>
                  <div className="space-y-3">
                    <Textarea
                      placeholder="Add your notes or edit the AI recommendations..."
                      className="min-h-[100px]"
                    />
                    <div className="flex gap-2">
                      <Button
                        // onClick={handleApprove}
                        className="!bg-green-600 !hover:bg-green-700"
                      >
                        <Check size={16} className="mr-2" />
                        Accept AI Recommendation
                      </Button>
                      <Button
                        variant="outline"
                        // onClick={handleReject}
                      >
                        <X size={16} className="mr-2" />
                        Override & Reject
                      </Button>
                      <Button
                        variant="outline"
                        // onClick={handleRequestInfo}
                      >
                        Request More Info
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Remaining sections unchanged */}
          {/* You can continue the conversion from here or ask me to complete the rest */}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Claim Information</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-600">
                    Claim ID
                  </label>
                  <p className="font-mono text-sm">{claimData?.claimId}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">
                    Customer
                  </label>
                  <p className="text-sm">{claimData?.name}</p>
                  <p className="text-xs text-slate-500">{claimData?.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">
                    Product
                  </label>
                  <p className="text-sm">{claimData?.product}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">
                    Issue Type
                  </label>
                  <p className="text-sm capitalize">{claimData?.issutype}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">
                    Claimed Amount
                  </label>
                  <p className="text-lg font-semibold text-slate-900">
                    {claimData?.ClaimAmount}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">
                    Filed Date
                  </label>
                  <p className="text-sm">11/07/2025</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Take Action</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!isApproved &&
                  !isRejected &&
                  !showAcceptForm &&
                  !showRejectForm && (
                    <>
                      <Button
                        className="w-full bg-success hover:bg-green-600 text-white"
                        onClick={() => setShowAcceptForm(true)}
                        disabled={isRequestSubmitted || showRequestInfoForm}
                      >
                        <Check size={16} className="mr-2" />
                        Approve Claim
                      </Button>

                      <Button
                        className="w-full bg-warning hover:bg-amber-600 text-white"
                        onClick={() => setShowRequestInfoForm(true)}
                        disabled={isRequestSubmitted}
                      >
                        <Clock size={16} className="mr-2" />
                        Request More Info
                      </Button>

                      <Button
                        className="w-full bg-error hover:bg-red-600 text-white"
                        onClick={() => setShowRejectForm(true)}
                        disabled={showRequestInfoForm}
                      >
                        <X size={16} className="mr-2" />
                        Reject Claim
                      </Button>
                    </>
                  )}

                {isApproved && (
                  <Button className="w-full bg-green-700 text-white" disabled>
                    <Check size={16} className="mr-2" /> Claim Approved
                  </Button>
                )}
                {requestConfirmationMsg && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Request More Info Note:
                    </p>
                    <p className="text-sm text-yellow-600 font-medium mt-2">
                      {requestConfirmationMsg}
                    </p>
                  </div>
                )}
                <FormProvider {...methods}>
                  {showRequestInfoForm && (
                    <div className="space-y-1">
                      <FormItem>
                        <FormLabel>Request Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="E.g. Need purchase receipt"
                            value={infoTitle}
                            onChange={(e) => setInfoTitle(e.target.value)}
                          />
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>Message to Customer</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Kindly provide the purchase invoice to proceed..."
                            value={infoMessage}
                            onChange={(e) => setInfoMessage(e.target.value)}
                          />
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>Required Documents</FormLabel>
                        <FormControl>
                          <Select
                            value={requiredDocs}
                            onValueChange={(value) => setRequiredDocs(value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select document" />
                            </SelectTrigger>
                            <SelectContent>
                              {DOCUMENT_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>Expected Response Time</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="25-June-2025"
                            value={responseTime}
                            onChange={(e) => setResponseTime(e.target.value)}
                          />
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>Internal Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Notes only for internal agents"
                            value={internalNotes}
                            onChange={(e) => setInternalNotes(e.target.value)}
                          />
                        </FormControl>
                      </FormItem>

                      <Button
                        className="w-full bg-blue-600 text-white"
                        onClick={handleSubmitRequestInfo}
                      >
                        Submit Request
                      </Button>
                    </div>
                  )}
                </FormProvider>
                <FormProvider {...methods}>
                  {showAcceptForm && !isApproved && (
                    <div className="space-y-1">
                      <FormItem>
                        <FormLabel>Acceptance Note</FormLabel>
                        <FormControl>
                          <Input
                            value={acceptReason}
                            onChange={(e) => setAcceptReason(e.target.value)}
                          />
                        </FormControl>
                      </FormItem>
                      <FormItem>
                        <FormLabel>
                          AI-Generated message for client to send
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            value={acceptMessage}
                            onChange={(e) => setAcceptMessage(e.target.value)}
                            className="rejectheight"
                          />
                        </FormControl>
                      </FormItem>
                      <Button
                        className="w-full bg-green-600 text-white"
                        onClick={handleAcceptSubmit}
                      >
                        Submit Approval
                      </Button>
                    </div>
                  )}
                </FormProvider>
                <FormProvider {...methods}>
                  {/* form items go here */}
                  {showRejectForm && !isRejected && (
                    <form onSubmit={methods.handleSubmit(handleRejectSubmit)}>
                      <FormItem>
                        <FormLabel>Rejection Reason</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Dishwasher Water Drain-Out Issue"
                            value="Dishwasher Water Drain-Out Issue"
                            readOnly
                          />
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>
                          AI-Generated message for client to send
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            value={rejectionMessage}
                            onChange={(e) => setRejectReason(e.target.value)}
                            className="rejectheight"
                          />
                        </FormControl>
                      </FormItem>

                      <Button
                        type="submit"
                        className="w-full bg-red-600 text-white"
                      >
                        Submit Rejection
                      </Button>
                    </form>
                  )}
                </FormProvider>
                {isApproved && (
                  <div className="space-y-1">
                    <p
                      className="text-sm font-medium"
                      style={{ marginTop: 15 }}
                    >
                      Acceptance Note:
                    </p>
                    <p className="text-sm text-green-600 font-medium whitespace-pre-line">
                      {acceptMessage}
                    </p>
                  </div>
                )}
                {isRejected && (
                  <div className="space-y-1">
                    <Button className="w-full bg-red-700 text-white" disabled>
                      <X size={16} className="mr-2" /> Claim Rejected
                    </Button>
                    <p
                      className="text-sm font-medium"
                      style={{ marginTop: 15 }}
                    >
                      Rejection Reason:
                    </p>
                    <p className="text-sm text-red-600 font-medium whitespace-pre-line">
                      {rejectionMessage}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center mb-2">
                  <label className="block text-sm font-medium text-slate-600">
                    Agent Notes
                  </label>

                  <Dialog
                    open={openSuggestionModal}
                    onOpenChange={setOpenSuggestionModal}
                  >
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className="ml-2 text-yellow-500 hover:text-yellow-600"
                        title="View suggestions"
                      >
                        <Lightbulb size={18} />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="w-full max-w-sm">
                      <h2 className="text-lg font-semibold mb-1">
                        Select AI suggestion
                      </h2>
                      <ul className="space-y-3">
                        {agentSuggestions.map((suggestion, idx) => (
                          <li key={idx}>
                            <button
                              onClick={() => {
                                setAgentNotes(suggestion);
                                setOpenSuggestionModal(false); // ✅ closes dialog
                              }}
                              className="w-full text-left bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-md text-sm"
                            >
                              {suggestion}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </DialogContent>
                  </Dialog>
                </div>

                <Textarea
                  rows={4}
                  placeholder="Add your review notes here..."
                  className="resize-none text-sm"
                  value={agentNotes}
                  onChange={(e) => setAgentNotes(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Troubleshoot Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
                    Questions
                  </div>
                  <span className="text-xs font-medium text-slate-500">
                    Status
                  </span>
                </div>

                <div className="space-y-3">
                  {triageData.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center text-sm text-slate-700"
                    >
                      <span className="w-3/4">{item.question}</span>
                      {item.answer && (
                        <span className="font-medium">{item.answer}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Claim Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-xaxathrive-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="text-white" size={12} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Claim submitted
                    </p>
                    <p className="text-xs text-slate-500">11/07/2025</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-info rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="text-white" size={12} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      AI analysis completed
                    </p>
                    <p className="text-xs text-slate-500">11/07/2025</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-warning rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="text-white" size={12} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Assigned to agent
                    </p>
                    <p className="text-xs text-slate-500">Now</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ClaimView;
